import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import type { ContractTemplate, FeeType } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { ProfileLink } from '@/components/ProfileLink';
import { trackEvent } from '@/lib/faro';

export default function TemplateDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const [error, setError] = useState('');
    const [newFeeType, setNewFeeType] = useState({ name: '', category: 'fee', default_amount: '', fee_trigger: 'manual' });

    const { data, isLoading } = useQuery({
        queryKey: ['template', id],
        queryFn: () => api.get<{ data: ContractTemplate }>(`/templates/${id}`),
    });

    const signMutation = useMutation({
        mutationFn: () => api.post('/contracts', { template_id: Number(id) }),
        onSuccess: () => {
            trackEvent('contract_signed', { template_id: Number(id) });
            navigate('/contracts');
        },
        onError: (err: Error) => setError(err.message),
    });

    const addFeeTypeMutation = useMutation({
        mutationFn: (data: { name: string; category: string; default_amount: number; fee_trigger: string }) =>
            api.post(`/templates/${id}/fee-types`, data),
        onSuccess: () => {
            setNewFeeType({ name: '', category: 'fee', default_amount: '', fee_trigger: 'manual' });
            queryClient.invalidateQueries({ queryKey: ['template', id] });
        },
        onError: (err: Error) => setError(err.message),
    });

    const deleteFeeTypeMutation = useMutation({
        mutationFn: (feeTypeId: number) =>
            api.delete(`/templates/${id}/fee-types/${feeTypeId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['template', id] });
        },
        onError: (err: Error) => setError(err.message),
    });

    const template = data?.data;

    if (isLoading) return <p className="text-sm text-slate-400">Loading...</p>;
    if (!template) return <p className="text-sm text-red-400">Template not found.</p>;

    const canSign = user && (user.role === 'sub' || user.role === 'switch' || user.role === 'admin');
    const isDom = user?.id === template.dom?.id;
    const feeTypes = template.fee_types ?? [];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-xl font-semibold">{template.title}</h1>
                {template.dom && (
                    <p className="text-sm text-slate-400">by <ProfileLink userId={template.dom.id} displayName={template.dom.display_name} /></p>
                )}
            </div>

            {template.description && (
                <p className="text-sm text-slate-300">{template.description}</p>
            )}

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Contract Terms</CardTitle>
                </CardHeader>
                <CardContent>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                        <div>
                            <dt className="text-slate-400">Opening Balance</dt>
                            <dd className="font-medium">{formatCurrency(template.opening_balance)}</dd>
                        </div>
                        <div>
                            <dt className="text-slate-400">Origination Fee</dt>
                            <dd className="font-medium">{formatCurrency(template.origination_fee)}</dd>
                        </div>
                        <div>
                            <dt className="text-slate-400">Interest Rate</dt>
                            <dd className="font-medium">{template.interest_rate}% APR</dd>
                        </div>
                        <div>
                            <dt className="text-slate-400">Compounding</dt>
                            <dd className="font-medium capitalize">{template.compounding_frequency}</dd>
                        </div>
                        <div>
                            <dt className="text-slate-400">Min Payment</dt>
                            <dd className="font-medium">{formatCurrency(template.minimum_payment)}</dd>
                        </div>
                        <div>
                            <dt className="text-slate-400">Max Payment</dt>
                            <dd className="font-medium">
                                {template.maximum_payment ? formatCurrency(template.maximum_payment) : 'None'}
                            </dd>
                        </div>
                        {template.admin_fee_amount && (
                            <>
                                <div>
                                    <dt className="text-slate-400">Admin Fee</dt>
                                    <dd className="font-medium">{formatCurrency(template.admin_fee_amount)}</dd>
                                </div>
                                <div>
                                    <dt className="text-slate-400">Fee Frequency</dt>
                                    <dd className="font-medium capitalize">{template.admin_fee_frequency}</dd>
                                </div>
                            </>
                        )}
                        {template.min_principal_payoff_pct && parseFloat(template.min_principal_payoff_pct) > 0 && (
                            <div>
                                <dt className="text-slate-400">Min Principal Payoff</dt>
                                <dd className="font-medium">{parseFloat(template.min_principal_payoff_pct)}%</dd>
                            </div>
                        )}
                    </dl>

                    <div className="mt-4 flex flex-wrap gap-2">
                        {template.allow_adhoc_fees && <Badge>Ad-hoc Fees</Badge>}
                        {template.hardcore_mode && <Badge variant="destructive">Hardcore Mode</Badge>}
                    </div>
                </CardContent>
            </Card>

            {/* Predefined fee types */}
            {(feeTypes.length > 0 || isDom) && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Predefined Fees & Taxes</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {feeTypes.length > 0 ? (
                            <div className="space-y-2">
                                {feeTypes.map((ft) => (
                                    <div key={ft.id} className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-800/50 px-3 py-2">
                                        <div>
                                            <span className="text-sm font-medium">{ft.name}</span>
                                            <Badge variant="secondary" className="ml-2 text-[10px]">
                                                {ft.category}
                                            </Badge>
                                            {ft.fee_trigger && ft.fee_trigger !== 'manual' && (
                                                <Badge variant="outline" className="ml-1 text-[10px] text-amber-400 border-amber-400/30">
                                                    auto: {ft.fee_trigger.replace('_', ' ')}
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-semibold">{formatCurrency(ft.default_amount)}</span>
                                            {isDom && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 px-2 text-xs text-red-400 hover:text-red-300"
                                                    onClick={() => deleteFeeTypeMutation.mutate(ft.id)}
                                                    disabled={deleteFeeTypeMutation.isPending}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500">No predefined fees or taxes.</p>
                        )}

                        {isDom && (
                            <div className="mt-4 space-y-3 border-t border-slate-800 pt-4">
                                <p className="text-xs text-slate-400">Add a fee or tax type</p>
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Name (e.g. Loser Tax)"
                                        value={newFeeType.name}
                                        onChange={(e) => setNewFeeType((f) => ({ ...f, name: e.target.value }))}
                                    />
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        placeholder="Amount"
                                        className="w-28"
                                        value={newFeeType.default_amount}
                                        onChange={(e) => setNewFeeType((f) => ({ ...f, default_amount: e.target.value }))}
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <select
                                        value={newFeeType.category}
                                        onChange={(e) => setNewFeeType((f) => ({ ...f, category: e.target.value }))}
                                        className="rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100"
                                    >
                                        <option value="fee">Fee</option>
                                        <option value="tax">Tax</option>
                                    </select>
                                    <select
                                        value={newFeeType.fee_trigger}
                                        onChange={(e) => setNewFeeType((f) => ({ ...f, fee_trigger: e.target.value }))}
                                        className="rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100"
                                    >
                                        <option value="manual">Manual</option>
                                        <option value="missed_payment">Auto: Missed Payment</option>
                                        <option value="minimum_only">Auto: Minimum Only</option>
                                        <option value="early_payoff">Auto: Early Payoff</option>
                                    </select>
                                    <Button
                                        size="sm"
                                        onClick={() => addFeeTypeMutation.mutate({
                                            name: newFeeType.name,
                                            category: newFeeType.category,
                                            default_amount: parseFloat(newFeeType.default_amount),
                                            fee_trigger: newFeeType.fee_trigger,
                                        })}
                                        disabled={!newFeeType.name || !newFeeType.default_amount || addFeeTypeMutation.isPending}
                                    >
                                        {addFeeTypeMutation.isPending ? 'Adding...' : 'Add'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {error && (
                <div className="rounded-md bg-red-900/30 p-3 text-sm text-red-400">{error}</div>
            )}

            {canSign && (
                <Button
                    className="w-full"
                    onClick={() => signMutation.mutate()}
                    disabled={signMutation.isPending}
                >
                    {signMutation.isPending ? 'Signing...' : 'Sign This Contract'}
                </Button>
            )}
        </div>
    );
}
